import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Logo } from '@/components/brand/Logo';

/**
 * Shared chrome for the public QuickBooks lifecycle pages
 * (`/quickbooks/launch`, `/quickbooks/connect`, `/quickbooks/disconnect`).
 *
 * These pages are public (no auth gate) because Intuit redirects users
 * here from outside Atlas — from the QBO "My Apps" page or the apps.com
 * marketplace listing — and reachable without authentication. The Atlas
 * sign-in chrome is rendered on the right rail so users coming from
 * Intuit can still reach the app shell.
 */
export function QuickbooksPageShell({
  eyebrow,
  title,
  description,
  children,
  footer,
}: {
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3" aria-label="Virro Atlas">
            <Logo variant="primary" tone="humus" className="h-7" />
          </Link>
          <Link
            to="/auth/sign-in"
            className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-xl space-y-8">
          <div className="space-y-3">
            {eyebrow && (
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {eyebrow}
              </p>
            )}
            <h1 className="text-3xl font-medium tracking-headline leading-headline">
              {title}
            </h1>
            {description && (
              <div className="text-sm leading-body text-muted-foreground">
                {description}
              </div>
            )}
          </div>
          {children && <div className="space-y-6">{children}</div>}
        </div>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-4xl flex-col gap-2 px-6 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>&copy; {new Date().getFullYear()} Eloo LLC d/b/a Virro Life.</p>
          <div className="flex gap-4">
            <Link to="/legal/privacy" className="hover:text-foreground">
              Privacy
            </Link>
            <Link to="/legal/eula" className="hover:text-foreground">
              EULA
            </Link>
          </div>
        </div>
        {footer && (
          <div className="mx-auto max-w-4xl px-6 pb-6 text-xs text-muted-foreground">
            {footer}
          </div>
        )}
      </footer>
    </div>
  );
}
